const TRACE_HEADER = 'X-TNS-Trace-ID';
const REQUEST_HEADER = 'X-Request-ID';
const NETWORK_IDLE_MS = 250;
const MAX_RECORDS = 500;
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const enabled = ['1', 'true', 'yes', 'on'].includes(
  String(process.env.REACT_APP_PERF_AUDIT || '').trim().toLowerCase(),
);
const moduleStartedAt =
  typeof performance !== 'undefined' ? performance.now() : Date.now();
const inFlight = new Map();
const state = {
  navigations: [],
  requests: [],
  endpointCounts: {},
  duplicateConcurrentRequests: 0,
  currentNavigation: null,
};

const now = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

const randomId = () => {
  const cryptoObject =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObject?.randomUUID) {
    return cryptoObject.randomUUID().replaceAll('-', '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
};

export const sanitizePerformanceId = (value) => {
  const candidate = String(value || '').trim();
  return ID_PATTERN.test(candidate) ? candidate : randomId().slice(0, 64);
};

const maskPathIdentifiers = (pathname) =>
  String(pathname || '/')
    .replace(/\/\d{6,}(?=\/|$)/g, '/:id')
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi,
      '/:id',
    )
    .replace(
      /\/[0-9a-f]{16,}(?=\/|$)/gi,
      '/:id',
    );

export const normalizePerformanceUrl = (value) => {
  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const url = new URL(String(value || '/'), base);
    const queryNames = [...new Set([...url.searchParams.keys()])].sort();
    const querySuffix = queryNames.length
      ? `?${queryNames.map((name) => `${name}=*`).join('&')}`
      : '';
    return `${maskPathIdentifiers(url.pathname)}${querySuffix}`;
  } catch {
    return maskPathIdentifiers(String(value || '/').split('?')[0]);
  }
};

const setHeader = (headers, name, value) => {
  if (headers?.set) {
    headers.set(name, value);
    return headers;
  }
  return {
    ...(headers || {}),
    [name]: value,
  };
};

const exposeState = () => {
  if (enabled && typeof window !== 'undefined') {
    window.__TNS_PERF_AUDIT__ = state;
  }
};

const trimRecords = (records) => {
  if (records.length > MAX_RECORDS) {
    records.splice(0, records.length - MAX_RECORDS);
  }
};

const createNavigation = (route, type = 'route') => ({
  id: sanitizePerformanceId(`nav-${randomId()}`),
  route: normalizePerformanceUrl(route),
  type,
  startedAt: type === 'hard' ? moduleStartedAt : now(),
  completedAt: null,
  totalMs: null,
  requestCount: 0,
  duplicateConcurrentRequests: 0,
  pendingRequests: 0,
  networkIdleMs: NETWORK_IDLE_MS,
  idleTimer: null,
});

const ensureNavigation = () => {
  if (!state.currentNavigation) {
    const route =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '/';
    state.currentNavigation = createNavigation(route, 'hard');
    state.navigations.push(state.currentNavigation);
    trimRecords(state.navigations);
    exposeState();
  }
  return state.currentNavigation;
};

const scheduleNetworkIdle = (navigation) => {
  if (!navigation || navigation.pendingRequests > 0 || navigation.completedAt) {
    return;
  }
  clearTimeout(navigation.idleTimer);
  navigation.idleTimer = setTimeout(() => {
    if (navigation.pendingRequests > 0 || navigation.completedAt) {
      return;
    }
    navigation.completedAt = now();
    navigation.totalMs = Number(
      (navigation.completedAt - navigation.startedAt).toFixed(3),
    );
    navigation.idleTimer = null;
    exposeState();
  }, NETWORK_IDLE_MS);
};

export const startPerformanceNavigation = (route) => {
  if (!enabled) return null;
  const normalizedRoute = normalizePerformanceUrl(route);
  const current = state.currentNavigation;
  if (
    current &&
    current.route === normalizedRoute &&
    !current.completedAt
  ) {
    return current.id;
  }
  if (current?.idleTimer) {
    clearTimeout(current.idleTimer);
    current.idleTimer = null;
  }
  state.currentNavigation = createNavigation(normalizedRoute);
  state.navigations.push(state.currentNavigation);
  trimRecords(state.navigations);
  scheduleNetworkIdle(state.currentNavigation);
  exposeState();
  return state.currentNavigation.id;
};

const parseServerTiming = (value) => {
  const output = {};
  for (const item of String(value || '').split(',')) {
    const [namePart, ...params] = item.trim().split(';');
    const name = String(namePart || '').trim();
    if (!name) continue;
    const durationParam = params.find((param) =>
      String(param).trim().startsWith('dur='),
    );
    const duration = Number(
      String(durationParam || '').trim().replace(/^dur=/, ''),
    );
    if (Number.isFinite(duration)) {
      output[name] = duration;
    }
  }
  return output;
};

export const instrumentPerformanceRequest = (config = {}) => {
  if (!enabled) return config;
  const navigation = ensureNavigation();
  const method = String(config.method || 'get').toUpperCase();
  const endpoint = normalizePerformanceUrl(
    `${String(config.baseURL || '')}${String(config.url || '')}`,
  );
  const requestId = sanitizePerformanceId(`req-${randomId()}`);
  const traceId = sanitizePerformanceId(navigation.id);
  const key = `${method} ${endpoint}`;
  const concurrent = inFlight.get(key) || 0;

  if (concurrent > 0) {
    state.duplicateConcurrentRequests += 1;
    navigation.duplicateConcurrentRequests += 1;
  }
  inFlight.set(key, concurrent + 1);
  navigation.requestCount += 1;
  navigation.pendingRequests += 1;
  clearTimeout(navigation.idleTimer);
  navigation.idleTimer = null;
  state.endpointCounts[key] = (state.endpointCounts[key] || 0) + 1;

  let headers = setHeader(config.headers, TRACE_HEADER, traceId);
  headers = setHeader(headers || config.headers, REQUEST_HEADER, requestId);
  config.headers = headers || config.headers;
  config.__tnsPerformance = {
    key,
    method,
    endpoint,
    requestId,
    traceId,
    navigationId: navigation.id,
    startedAt: now(),
  };
  exposeState();
  return config;
};

const findNavigation = (navigationId) =>
  state.navigations.find((item) => item.id === navigationId);

const finishRequest = (config, response, error) => {
  if (!enabled) return;
  const metadata = config?.__tnsPerformance;
  if (!metadata) return;

  const concurrent = Math.max(0, (inFlight.get(metadata.key) || 1) - 1);
  if (concurrent > 0) {
    inFlight.set(metadata.key, concurrent);
  } else {
    inFlight.delete(metadata.key);
  }

  const navigation = findNavigation(metadata.navigationId);
  if (navigation) {
    navigation.pendingRequests = Math.max(0, navigation.pendingRequests - 1);
  }
  const responseHeaders = response?.headers || {};
  const serverTiming =
    responseHeaders['server-timing'] ||
    responseHeaders.get?.('server-timing') ||
    '';
  state.requests.push({
    requestId: metadata.requestId,
    traceId: metadata.traceId,
    navigationId: metadata.navigationId,
    method: metadata.method,
    endpoint: metadata.endpoint,
    durationMs: Number((now() - metadata.startedAt).toFixed(3)),
    status: Number(response?.status || error?.response?.status || 0),
    ok: !error,
    serverTiming: parseServerTiming(serverTiming),
  });
  trimRecords(state.requests);
  scheduleNetworkIdle(navigation);
  exposeState();
};

export const instrumentPerformanceResponse = (response) => {
  finishRequest(response?.config, response, null);
  return response;
};

export const instrumentPerformanceError = (error) => {
  finishRequest(error?.config, error?.response, error);
  return Promise.reject(error);
};

export const getPerformanceAuditState = () => state;
export const isPerformanceAuditEnabled = () => enabled;
